import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import TeamsSidebar from "./Sidebar";
import { Volume2, Volume1, ChevronLeft, ChevronRight, Menu, X, Lightbulb, ChevronRight as ChevronRightIcon, ChevronLeft as ChevronLeftIcon } from "lucide-react";
import TopBar from "./TopBar";
import FooterPagination from "./FooterPagination";
import FullscreenMessages from './FullscreenMessages';
import FloatingChatbot from './FloatingChatbot';
import GlobalAudioPlayer from "./GlobalAudioPlayer";
import NotificationBanner from './NotificationBanner';
import AlertsPanel from './AlertsPanel';
import ResizablePanel from './ResizablePanel';
import axios from "axios";
import { useAuth } from "../AuthContext";
import MFAReminderModal from "../MFAReminderModal";
import logger from "../../utils/logger";
const channelColors = {
  0: "#f43f5e",
  1: "#ff8b2d",
  2: "#2d84ff",
  3: "#34d399",
  4: "#6b7280",
};
const ITEMS_PER_PAGE = 100;
const TIME_FILTERS = {
  ALL: 'All',
  '30MIN': 'Last 30 mins',
  '1HOUR': 'Last 1 hour',
  '2HOUR': 'Last 2 hours',
  '4HOUR': 'Last 4 hours',
  '8HOUR': 'Last 8 hours',
  '24HOUR': 'Last 1 Day',
  '48HOUR': 'Last 2 Days',
  '168HOUR': 'Last Week',
};

const LiveCommunications = ({ edgeServerEndpoint, toggleTheme, channels, setChannels, timezone, timeFormat, isDarkMode, setMessages, setIsDarkMode, messages, keywords, reverseSort, setReverseSort, onFiltersActiveChange }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeKeywords, setActiveKeywords] = useState(new Set());
  const [showMfaReminder, setShowMfaReminder] = useState(false);
  const [mfaEnforced, setMfaEnforced] = useState(false);
  const [isVolumeOn, setIsVolumeOn] = useState(false);
  const [keywordCounts, setKeywordCounts] = useState({});
  const [playingAudio, setPlayingAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [timeFilter, setTimeFilter] = useState(() => {
    const savedTimeFilter = localStorage.getItem('timeFilter');
    return savedTimeFilter || TIME_FILTERS.ALL;
  });
  const [activeAudioUrl, setActiveAudioUrl] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const audioRef = useRef(new Audio());
  const [iconToggle, setIconToggle] = useState(false);
  const [channelMessageCounts, setChannelMessageCounts] = useState({});
  const [activeChannels, setActiveChannels] = useState(
    Object.keys(channels).reduce((acc, channelId) => {
      acc[channelId] = channels[channelId]?.isActive || true;
      return acc;
    }, {})
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [previousMessages, setPreviousMessages] = useState([]);
  const [newMessages, setNewMessages] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [showFullTimestamps, setShowFullTimestamps] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;
  const [userRole, setUserRole] = useState('member');
  const [inboxViewMode, setInboxViewMode] = useState('continuous'); // 'pagination' or 'continuous'
  
  // Alerts panel state
  const [isAlertsVisible, setIsAlertsVisible] = useState(() => {
    const saved = localStorage.getItem('alertsPanelVisible');
    return saved !== null ? saved === 'true' : true; // Default to visible
  });
  
  // Sidebar visibility state
  const [isSidebarVisible, setIsSidebarVisible] = useState(() => {
    const saved = localStorage.getItem('sidebarVisible');
    return saved !== null ? saved === 'true' : true; // Default to visible
  });
  
  // Panel widths state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 288; // Default 288px (w-72)
  });
  
  const [alertsWidth, setAlertsWidth] = useState(() => {
    const saved = localStorage.getItem('alertsWidth');
    return saved ? parseInt(saved, 10) : 288; // Default 288px (w-72)
  });

  // Calculate messages panel width (remaining space)
  // Account for minimum section widths in TopBar when panels are hidden
  const MIN_CHANNELS_WIDTH = 250; // Minimum width for Channels section in TopBar
  const MIN_INSIGHTS_WIDTH = 200; // Minimum width for Insights section in TopBar
  const messagesWidth = useMemo(() => {
    if (windowWidth === 0) return 0;
    const channelsSectionWidth = isSidebarVisible ? sidebarWidth : MIN_CHANNELS_WIDTH;
    const insightsSectionWidth = isAlertsVisible ? alertsWidth : MIN_INSIGHTS_WIDTH;
    return windowWidth - channelsSectionWidth - insightsSectionWidth;
  }, [windowWidth, isSidebarVisible, sidebarWidth, isAlertsVisible, alertsWidth]);
  
  // Infinite scroll state for mobile
  const [mobileMessages, setMobileMessages] = useState([]);
  const [mobileCurrentPage, setMobileCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  
  // Infinite scroll state for desktop
  const [desktopMessages, setDesktopMessages] = useState([]);
  const [desktopCurrentPage, setDesktopCurrentPage] = useState(1);
  
  // Track total filtered messages count
  const [totalFilteredMessages, setTotalFilteredMessages] = useState(0);
  
  // Custom filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  // Save panel visibility preferences
  useEffect(() => {
    localStorage.setItem('alertsPanelVisible', isAlertsVisible.toString());
  }, [isAlertsVisible]);
  
  useEffect(() => {
    localStorage.setItem('sidebarVisible', isSidebarVisible.toString());
  }, [isSidebarVisible]);
  
  // Quick show/hide handlers
  const toggleSidebarQuick = useCallback(() => {
    setIsSidebarVisible(prev => !prev);
  }, []);
  
  const toggleAlertsQuick = useCallback(() => {
    setIsAlertsVisible(prev => !prev);
  }, []);

  // Notify parent when filters are active/inactive
  useEffect(() => {
    if (onFiltersActiveChange) {
      const filtersActive = !!(startDate || endDate || (timeFilter && timeFilter !== TIME_FILTERS.ALL));
      onFiltersActiveChange(filtersActive);
    }
  }, [startDate, endDate, timeFilter, onFiltersActiveChange]);

  const { user: authUser, isReLogin, resetReLoginFlag } = useAuth();

  // Save pagination preferences to backend
  const savePaginationPreferences = useCallback(async (newRecordsPerPage, newCurrentPage, newReverseSort, newShowFullTimestamps) => {
    if (!authUser?.username) return;
    
    try {
      await axios.post(`${edgeServerEndpoint}/pagination-preferences/${authUser.username}`, {
        recordsPerPage: newRecordsPerPage || recordsPerPage,
        currentPage: newCurrentPage || currentPage,
        reverseSort: newReverseSort !== undefined ? newReverseSort : reverseSort,
        showFullTimestamps: newShowFullTimestamps !== undefined ? newShowFullTimestamps : showFullTimestamps
      });
    } catch (error) {
      logger.error('Failed to save pagination preferences:', error);
    }
  }, [authUser?.username, edgeServerEndpoint, recordsPerPage, currentPage, reverseSort, showFullTimestamps]);

  // Save reverse sort preference when it changes
  const saveReverseSortPreference = useCallback(async (newReverseSort) => {
    await savePaginationPreferences(recordsPerPage, currentPage, newReverseSort);
  }, [savePaginationPreferences, recordsPerPage, currentPage]);

  // Custom setter for reverseSort that saves the preference
  const setReverseSortWithSave = useCallback((newReverseSort) => {
    setReverseSort(newReverseSort);
    saveReverseSortPreference(newReverseSort);
  }, [setReverseSort, saveReverseSortPreference]);

  // Save showFullTimestamps preference when it changes (only after initial load)
  useEffect(() => {
    if (paginationLoaded && authUser?.username) {
      savePaginationPreferences(recordsPerPage, currentPage, reverseSort, showFullTimestamps);
    }
  }, [showFullTimestamps, paginationLoaded, authUser?.username, savePaginationPreferences, recordsPerPage, currentPage, reverseSort]);

  // Check for MFA reminder on mount
  useEffect(() => {
    let isMounted = true;
    let timeout;

    const checkMfaReminder = () => {
      // Early return if no user - don't make API call
      if (!authUser?.username) {
        return;
      }
      
      const token = localStorage.getItem('token');
      // Early return if no token - don't make API call
      if (!token) {
        return;
      }

      // edgeServerEndpoint already includes /api prefix, so don't add it again
      const url = edgeServerEndpoint.startsWith('/api') 
        ? `${edgeServerEndpoint}/mfa/status`
        : `${edgeServerEndpoint}/api/mfa/status`;
      
      // Use .then/.catch chain instead of await to prevent unhandled rejections
      fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        // Check if response is actually JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          // Server returned HTML (likely 404 page), not JSON
          logger.debug('MFA status endpoint returned non-JSON response');
          return null;
        }

        if (response.ok) {
          return response.json().then(data => {
            // Show reminder if MFA is enforced but not enabled
            if (isMounted && data.mfa_enforced && !data.mfa_enabled) {
              // Check if user has dismissed it for this session
              const dismissed = sessionStorage.getItem('mfa_reminder_dismissed');
              if (!dismissed) {
                setMfaEnforced(true);
                setShowMfaReminder(true);
              }
            }
          });
        } else if (response.status === 401) {
          // 401 is expected for non-admin users or when token is invalid
          // Don't log as error or warning - this is normal
        } else {
          // Only log non-401 errors
          logger.warn(`MFA status endpoint returned ${response.status}`);
        }
      })
      .catch(err => {
        // Network errors during fetch - silently fail for MFA status
        // These are expected failures when token is invalid or unavailable
        // Don't log anything
      });
    };
    
    // Only check if user exists and has username
    // Use authUser?.username as dependency instead of entire authUser object to prevent unnecessary re-runs
    if (authUser?.username) {
      // Debounce: only check after 500ms of no changes
      timeout = setTimeout(checkMfaReminder, 500);
    }

    return () => {
      isMounted = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [authUser?.username, edgeServerEndpoint]);

  // Load pagination preferences from backend
  useEffect(() => {
    const loadPaginationPreferences = async () => {
      if (!authUser?.username || paginationLoaded) return;
      
      try {
        const response = await axios.get(`${edgeServerEndpoint}/pagination-preferences/${authUser.username}`);
        if (response.data) {
          const { recordsPerPage: savedRecordsPerPage, currentPage: savedCurrentPage, reverseSort: savedReverseSort, showFullTimestamps: savedShowFullTimestamps } = response.data;
          if (savedRecordsPerPage) {
            setRecordsPerPage(savedRecordsPerPage);
          }
          if (savedCurrentPage) {
            setCurrentPage(savedCurrentPage);
          }
          if (savedReverseSort !== undefined) {
            setReverseSort(savedReverseSort);
          }
          if (savedShowFullTimestamps !== undefined) {
            setShowFullTimestamps(savedShowFullTimestamps);
          }
        }
      } catch (error) {
        logger.error('Failed to load pagination preferences:', error);
        // Use default values if loading fails
        setRecordsPerPage(20);
        setCurrentPage(1);
        setReverseSort(false);
      } finally {
        setPaginationLoaded(true);
      }
    };

    loadPaginationPreferences();
  }, [authUser?.username, edgeServerEndpoint, paginationLoaded, setReverseSort]);

  // Load global inbox view mode and default records-per-page from settings
  useEffect(() => {
    const loadInboxSettings = async () => {
      try {
        const response = await axios.get(`${edgeServerEndpoint}/settings`);
        const mode = response.data?.global_inbox_view_mode || 'pagination';
        const defaultPageSize = response.data?.global_inbox_records_per_page || 10;
        setInboxViewMode(mode);
        // Only override recordsPerPage if the user hasn't already saved a preference
        setRecordsPerPage(prev => (prev === 20 ? defaultPageSize : prev));
      } catch (error) {
        logger.error('Failed to load inbox settings:', error);
        // Fallback: keep existing state (infinite scroll behaviour)
      }
    };

    if (edgeServerEndpoint) {
      loadInboxSettings();
    }
  }, [edgeServerEndpoint]);

  // Handle re-login scenario - automatically redirect to latest messages
  useEffect(() => {
    if (isReLogin && paginationLoaded) {
      // Set reverse sort to true to show latest messages first
      setReverseSortWithSave(true);
      // Set current page to 1 to show the first page (which will be the latest messages)
      setCurrentPage(1);
      // Save the new preferences
      savePaginationPreferences(recordsPerPage, 1, true);
      // Reset the re-login flag
      resetReLoginFlag();
    }
  }, [isReLogin, paginationLoaded, setReverseSortWithSave, savePaginationPreferences, recordsPerPage, resetReLoginFlag]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to filter messages (defined early for use in effects)
  const getFilteredMessages = useCallback(() => {
    let filtered = messages.filter((message) => {
      const channelActive = activeChannels[message.channel];
      const matchesSearch = searchQuery
        ? message.message.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      const matchesKeywords =
        activeKeywords.size === 0 ||
        [...activeKeywords].some((keyword) =>
          message.message.toLowerCase().includes(keyword.toLowerCase())
        );
      return channelActive && matchesSearch && matchesKeywords;
    });
    filtered = filterMessagesByTime(filtered);
    
    if (reverseSort) {
      // For reverse sorting, we want newest first, so we don't reverse
      return filtered;
    } else {
      // For normal sorting, we want oldest first, so we reverse
      return filtered.slice().reverse();
    }
  }, [messages, activeChannels, activeKeywords, searchQuery, timeFilter, reverseSort, startDate, endDate, startTime, endTime]);

  // Helper function to filter messages for mobile (defined early for use in effects)
  const getFilteredMessagesForMobile = useCallback(() => {
    let filtered = messages.filter(msg => {
      const channelActive = activeChannels[msg.channel] !== false;
      const keywordMatch = activeKeywords.size === 0 || 
        Array.from(activeKeywords).some(kw => 
          msg.message.toLowerCase().includes(kw.toLowerCase())
        );
      return channelActive && keywordMatch;
    });
    
    // Apply custom date range filter if set
    if (startDate && endDate) {
      let start, end;
      
      // Helper function to convert local date/time to UTC Date object
      const localToUTC = (dateStr, timeStr) => {
        // Parse date components (YYYY-MM-DD format)
        const [year, month, day] = dateStr.split('-').map(Number);
        // Parse time components (HH:MM format)
        const [hours = 0, minutes = 0, seconds = 0] = timeStr ? timeStr.split(':').map(Number) : [0, 0, 0];
        
        // Create Date object using local timezone components
        // This creates a date representing the local time entered by the user
        // JavaScript stores this internally as UTC milliseconds automatically
        const localDate = new Date(year, month - 1, day, hours, minutes, seconds);
        
        // The Date object already has the correct UTC representation internally
        // We can use it directly for comparison with UTC timestamps
        return localDate;
      };
      
      // Build start datetime in user's local timezone
      if (startTime) {
        start = localToUTC(startDate, startTime);
      } else {
        start = localToUTC(startDate, '00:00:00');
      }
      
      // Build end datetime in user's local timezone
      if (endTime) {
        const [hours, minutes] = endTime.split(':').map(Number);
        end = localToUTC(endDate, `${hours}:${minutes}:59`);
        end.setMilliseconds(999);
      } else {
        end = localToUTC(endDate, '23:59:59');
        end.setMilliseconds(999);
      }
      
      filtered = filtered.filter(msg => {
        const msgTime = parseTimestamp(msg.time); // Already in UTC
        // Both start/end (from local user input) and msgTime (from UTC database) 
        // are Date objects with UTC milliseconds internally, so comparison works correctly
        const isInRange = msgTime >= start && msgTime <= end;
        return isInRange;
      });
    } else {
      // Apply time filter only if custom date range is not set
      if (timeFilter !== TIME_FILTERS.ALL) {
        const now = new Date();
        let cutoff;
        switch (timeFilter) {
          case TIME_FILTERS['30MIN']:
            cutoff = new Date(now - 30 * 60 * 1000);
            break;
          case TIME_FILTERS['1HOUR']:
            cutoff = new Date(now - 60 * 60 * 1000);
            break;
          case TIME_FILTERS['2HOUR']:
            cutoff = new Date(now - 2 * 60 * 60 * 1000);
            break;
          case TIME_FILTERS['4HOUR']:
            cutoff = new Date(now - 4 * 60 * 60 * 1000);
            break;
          case TIME_FILTERS['8HOUR']:
            cutoff = new Date(now - 8 * 60 * 60 * 1000);
            break;
          case TIME_FILTERS['24HOUR']:
            cutoff = new Date(now - 24 * 60 * 60 * 1000);
            break;
          case TIME_FILTERS['48HOUR']:
            cutoff = new Date(now - 48 * 60 * 60 * 1000);
            break;
          case TIME_FILTERS['168HOUR']:
            cutoff = new Date(now - 168 * 60 * 60 * 1000);
            break;
          default:
            cutoff = null;
        }
        if (cutoff) {
          filtered = filtered.filter(msg => {
            const msgTime = parseTimestamp(msg.time); // Already in UTC
            return msgTime >= cutoff;
          });
        }
      }
    }
    
    // Apply sorting
    if (reverseSort) {
      return filtered;
    } else {
      return filtered.slice().reverse();
    }
  }, [messages, activeChannels, activeKeywords, timeFilter, reverseSort, startDate, endDate, startTime, endTime]);

  // Initialize messages according to view mode
  useEffect(() => {
    if (isMobile) {
      const filtered = getFilteredMessagesForMobile();
      setTotalFilteredMessages(filtered.length);
      let initialMessages;
      if (inboxViewMode === 'pagination') {
        // For pagination mode, use the current page
        initialMessages = filtered.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);
      } else {
        // For continuous mode
        if (reverseSort) {
          // reverseSort=true: newest at top, start from beginning
          initialMessages = filtered.slice(0, recordsPerPage);
        } else {
          // reverseSort=false: newest at bottom, start from end (show most recent messages)
          const startIndex = Math.max(0, filtered.length - recordsPerPage);
          initialMessages = filtered.slice(startIndex);
        }
      }
      setMobileMessages(initialMessages);
      setMobileCurrentPage(1);
      // For continuous mode, hasMore depends on sort order
      if (inboxViewMode === 'continuous') {
        if (reverseSort) {
          // reverseSort=true: loading more means going forward in array
          setHasMoreMessages(filtered.length > recordsPerPage);
        } else {
          // reverseSort=false: loading more means going backward in array
          const startIndex = Math.max(0, filtered.length - recordsPerPage);
          setHasMoreMessages(startIndex > 0);
        }
      } else {
        setHasMoreMessages(false);
      }
    } else {
      const filtered = getFilteredMessages();
      setTotalFilteredMessages(filtered.length);
      let initialMessages;
      if (inboxViewMode === 'pagination') {
        // For pagination mode, use the current page
        initialMessages = filtered.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);
      } else {
        // For continuous mode
        if (reverseSort) {
          // reverseSort=true: newest at top, start from beginning
          initialMessages = filtered.slice(0, recordsPerPage);
        } else {
          // reverseSort=false: newest at bottom, start from end (show most recent messages)
          const startIndex = Math.max(0, filtered.length - recordsPerPage);
          initialMessages = filtered.slice(startIndex);
        }
      }
      setDesktopMessages(initialMessages);
      setDesktopCurrentPage(1);
      // For continuous mode, hasMore depends on sort order
      if (inboxViewMode === 'continuous') {
        if (reverseSort) {
          // reverseSort=true: loading more means going forward in array
          setHasMoreMessages(filtered.length > recordsPerPage);
        } else {
          // reverseSort=false: loading more means going backward in array
          const startIndex = Math.max(0, filtered.length - recordsPerPage);
          setHasMoreMessages(startIndex > 0);
        }
      } else {
        setHasMoreMessages(false);
      }
    }
  }, [isMobile, getFilteredMessagesForMobile, getFilteredMessages, recordsPerPage, currentPage, inboxViewMode, reverseSort]);

  // Load more messages for continuous scroll (both mobile and desktop)
  const loadMoreMessages = useCallback(() => {
    if (inboxViewMode !== 'continuous') return;
    if (isLoadingMore || !hasMoreMessages) return;
    
    setIsLoadingMore(true);
    
    // Simulate a small delay for better UX
    setTimeout(() => {
      if (isMobile) {
        const filtered = getFilteredMessagesForMobile();
        setTotalFilteredMessages(filtered.length);
        const nextPage = mobileCurrentPage + 1;
        let newMessages;
        let hasMore;
        
        if (reverseSort) {
          // reverseSort=true: newest at top, load more from beginning
          const endIndex = nextPage * recordsPerPage;
          newMessages = filtered.slice(0, endIndex);
          hasMore = endIndex < filtered.length;
        } else {
          // reverseSort=false: newest at bottom, load more from end (going backward)
          const currentStartIndex = Math.max(0, filtered.length - (mobileCurrentPage * recordsPerPage));
          const newStartIndex = Math.max(0, currentStartIndex - recordsPerPage);
          newMessages = filtered.slice(newStartIndex);
          hasMore = newStartIndex > 0;
        }
        
        setMobileMessages(newMessages);
        setMobileCurrentPage(nextPage);
        setHasMoreMessages(hasMore);
      } else {
        const filtered = getFilteredMessages();
        setTotalFilteredMessages(filtered.length);
        const nextPage = desktopCurrentPage + 1;
        let newMessages;
        let hasMore;
        
        if (reverseSort) {
          // reverseSort=true: newest at top, load more from beginning
          const endIndex = nextPage * recordsPerPage;
          newMessages = filtered.slice(0, endIndex);
          hasMore = endIndex < filtered.length;
        } else {
          // reverseSort=false: newest at bottom, load more from end (going backward)
          const currentStartIndex = Math.max(0, filtered.length - (desktopCurrentPage * recordsPerPage));
          const newStartIndex = Math.max(0, currentStartIndex - recordsPerPage);
          newMessages = filtered.slice(newStartIndex);
          hasMore = newStartIndex > 0;
        }
        
        setDesktopMessages(newMessages);
        setDesktopCurrentPage(nextPage);
        setHasMoreMessages(hasMore);
      }
      setIsLoadingMore(false);
    }, 300);
  }, [inboxViewMode, isLoadingMore, hasMoreMessages, isMobile, mobileCurrentPage, desktopCurrentPage, recordsPerPage, getFilteredMessagesForMobile, getFilteredMessages, reverseSort]);

  const API_BASE_URL = `${edgeServerEndpoint}`;

  const [branding, setBranding] = useState({
    organizationName: 'Boondock Edge',
    tagline: 'Justice in Motion',
    brandColors: { accent: '#ff2424', primary: '#0a58ff', secondary: '#b15990' },
    font: 'Poppins',
    assets: { logo: null, favicon: null, loader: null }
  });
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.username || !edgeServerEndpoint) {
        return;
      }
      try {
        const response = await axios.get(`${edgeServerEndpoint}/users/${user.username}`);
        const role = response.data?.[user.username]?.role || 'member';
        setUserRole(role);
      } catch (error) {
        logger.error('Failed to fetch user role:', error);
        setUserRole('member');
      }
    };

    fetchUserRole();
  }, [user?.username, edgeServerEndpoint]);

  
  // Toggle sidebar and log state for debugging
  const toggleSidebar = (e) => {
    e.stopPropagation();
    logger.debug("Menu button clicked, current isSidebarOpen:", isSidebarOpen);
    setIsSidebarOpen(prev => {
      const newState = !prev;
      logger.debug("New sidebar state:", newState);
      return newState;
    });
  };

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (isMobile && isSidebarOpen && !e.target.closest('.sidebar-container')) {
        logger.debug("Clicked outside, closing sidebar");
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isMobile, isSidebarOpen]);

  const fetchBrandingData = async () => {
    try {
      const response = await fetch(`${edgeServerEndpoint}/branding`);
      if (!response.ok) throw new Error('Failed to fetch branding data');
      const data = await response.json();
      setBranding({
        organizationName: data.organization_name || 'Boondock Edge',
        tagline: data.tagline || 'Justice in Motion',
        channels_label: data.channels_label || 'Channels',
        brandColors: {
          accent: data.brand_colors?.accent || '#ff2424',
          primary: data.brand_colors?.primary || '#0a58ff',
          secondary: data.brand_colors?.secondary || '#b15990'
        },
        font: data.font || 'Poppins',
        assets: {
          logo: data.assets?.logo ? `data:image/jpeg;base64,${data.assets.logo}` : null,
          favicon: data.assets?.favicon ? `data:image/x-icon;base64,${data.assets.favicon}` : null,
          loader: data.assets?.loader ? `data:image/gif;base64,${data.assets.loader}` : null
        }
      });
    } catch (error) {
      logger.error('Error fetching branding data:', error);
    } finally {
      setBrandingLoaded(true);
    }
  };

  useEffect(() => {
    fetchBrandingData();
  }, [edgeServerEndpoint]);

  // Listen for branding updates
  useEffect(() => {
    const handleBrandingUpdate = () => {
      fetchBrandingData();
    };
    window.addEventListener('brandingUpdated', handleBrandingUpdate);
    return () => {
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
    };
  }, [edgeServerEndpoint]);

  useEffect(() => {
    if (brandingLoaded && branding.assets.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = branding.assets.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [brandingLoaded, branding.assets.favicon]);

  const filterMessagesByTime = (messages) => {
    // Apply custom date range filter if set
    if (startDate && endDate) {
      let start, end;
      
      // Helper function to convert local date/time to UTC
      const localToUTC = (dateStr, timeStr) => {
        // Parse date components
        const [year, month, day] = dateStr.split('-').map(Number);
        // Parse time components
        const [hours = 0, minutes = 0, seconds = 0] = timeStr ? timeStr.split(':').map(Number) : [0, 0, 0];
        
        // Create Date object in local timezone using Date constructor
        // This creates a date in the user's local timezone
        const localDate = new Date(year, month - 1, day, hours, minutes, seconds);
        
        // The Date object stores time internally as UTC milliseconds
        // localDate.getTime() already gives us UTC milliseconds
        // So we can use it directly - no conversion needed!
        // The Date object handles the timezone conversion automatically
        return new Date(localDate.getTime());
      };
      
      // Build start datetime in user's local timezone, then convert to UTC
      if (startTime) {
        // User enters date and time in local timezone
        start = localToUTC(startDate, startTime);
      } else {
        // Just date, start of day in local timezone
        start = localToUTC(startDate, '00:00:00');
      }
      
      // Build end datetime in user's local timezone, then convert to UTC
      if (endTime) {
        // User enters date and time in local timezone
        const [hours, minutes] = endTime.split(':').map(Number);
        end = localToUTC(endDate, `${hours}:${minutes}:59`);
        end.setMilliseconds(999);
      } else {
        // Just date, end of day in local timezone
        end = localToUTC(endDate, '23:59:59');
        end.setMilliseconds(999);
      }
      
      return messages.filter(message => {
        const messageTime = parseTimestamp(message.time); // Already in UTC
        // Both start/end (from local user input) and messageTime (from UTC database) 
        // are Date objects with UTC milliseconds internally, so comparison works correctly
        const isInRange = messageTime >= start && messageTime <= end;
        return isInRange;
      });
    }
    
    // Otherwise apply time filter
    if (timeFilter === TIME_FILTERS.ALL) return messages;
    const now = new Date();
    const filterMinutes = {
      [TIME_FILTERS['30MIN']]: 30,
      [TIME_FILTERS['1HOUR']]: 60,
      [TIME_FILTERS['2HOUR']]: 120,
      [TIME_FILTERS['4HOUR']]: 240,
      [TIME_FILTERS['8HOUR']]: 480,
      [TIME_FILTERS['24HOUR']]: 1440,
      [TIME_FILTERS['48HOUR']]: 2880,
      [TIME_FILTERS['168HOUR']]: 10080,
    }[timeFilter];
    const cutoffTime = new Date(now - filterMinutes * 60000);
    return messages.filter(message => {
      const messageTime = parseTimestamp(message.time);
      return messageTime >= cutoffTime;
    });
  };

  useEffect(() => {
    localStorage.setItem('timeFilter', timeFilter);
  }, [timeFilter]);

  useEffect(() => {
    const filteredMessages = filterMessagesByTime(messages);
    const counts = {};
    filteredMessages.forEach((message) => {
      counts[message.channel] = (counts[message.channel] || 0) + 1;
    });
    setChannelMessageCounts(counts);
  }, [messages, timeFilter, startDate, endDate, startTime, endTime]);

  const getTotalPages = (messagesCount) => Math.ceil(messagesCount / recordsPerPage);

  // Adjust current page when sort preference changes or when messages are first loaded (only for pagination mode)
  useEffect(() => {
    if (!paginationLoaded || inboxViewMode !== 'pagination' || messages.length === 0) return;
    
    const filtered = isMobile ? getFilteredMessagesForMobile() : getFilteredMessages();
    const totalPages = getTotalPages(filtered.length);
    
    if (totalPages === 0) return;
    
    // When reverseSort=false (newest at bottom), ensure we're on the last page if we're on page 1
    // When reverseSort=true (newest at top), ensure we're on the first page if we're on the last page
    // This ensures users see the newest messages by default
    if (!reverseSort && currentPage === 1 && totalPages > 1) {
      // User wants newest at bottom, so go to last page to show newest messages
      const lastPage = totalPages;
      setCurrentPage(lastPage);
      savePaginationPreferences(recordsPerPage, lastPage, reverseSort);
    } else if (reverseSort && currentPage === totalPages && totalPages > 1) {
      // User wants newest at top, so go to first page to show newest messages
      setCurrentPage(1);
      savePaginationPreferences(recordsPerPage, 1, reverseSort);
    } else if (currentPage > totalPages && totalPages > 0) {
      // If current page is beyond total pages (e.g., after filtering), go to the appropriate default page
      const targetPage = reverseSort ? 1 : totalPages;
      setCurrentPage(targetPage);
      savePaginationPreferences(recordsPerPage, targetPage, reverseSort);
    }
  }, [paginationLoaded, reverseSort, inboxViewMode, messages.length, isMobile, getFilteredMessagesForMobile, getFilteredMessages, recordsPerPage, currentPage, savePaginationPreferences]);

  const handlePlayAudio = (url) => {
    if (playingAudio === url) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    } else {
      if (audioRef.current) audioRef.current.pause();
      audioRef.current.src = url;
      audioRef.current.play();
      setPlayingAudio(url);
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    audioRef.current.onended = () => {
      setIsPlaying(false);
      setPlayingAudio(null);
    };
  }, []);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => setIconToggle(prev => !prev), 500);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  useEffect(() => {
    if (!keywords?.length) return;
    const filteredMessages = filterMessagesByTime(messages);
    const counts = {};
    keywords.forEach(keyword => {
      let count = 0;
      filteredMessages.forEach(msg => {
        const regex = new RegExp(keyword, 'gi');
        const matches = msg.message.match(regex);
        if (matches) count += matches.length;
      });
      if (count > 0) counts[keyword] = count;
    });
    setKeywordCounts(counts);
  }, [messages, keywords, timeFilter]);


  const AudioIcon = ({ url }) => {
    const isCurrentlyPlaying = playingAudio === url && isPlaying;
    return isCurrentlyPlaying ? (
      iconToggle ? (
        <Volume2 className="inline ml-2 text-blue-500 cursor-pointer" size={18} />
      ) : (
        <Volume1 className="inline ml-2 text-blue-500 cursor-pointer" size={18} />
      )
    ) : (
      <Volume1 className="inline ml-2 text-gray-400 cursor-pointer" size={18} />
    );
  };

  const toggleKeyword = (keywordId) => {
    setActiveKeywords((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(keywordId)) newSet.delete(keywordId);
      else newSet.add(keywordId);
      return newSet;
    });
  };

  const parseTimestamp = (timestamp) => {
    const year = parseInt(timestamp.substring(0, 4));
    const month = parseInt(timestamp.substring(4, 6)) - 1;
    const day = parseInt(timestamp.substring(6, 8));
    const hours = parseInt(timestamp.substring(9, 11));
    const minutes = parseInt(timestamp.substring(11, 13));
    const seconds = parseInt(timestamp.substring(13, 15));
    const utcDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    return utcDate;
  };

  // Helper function to format time display based on time format preference
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '';
    if (timeFormat === '24h') return timeStr; // Already in 24h format
    
    // Convert 24h format (HH:MM:SS) to 12h format (HH:MM:SS AM/PM)
    const [hours, minutes, seconds] = timeStr.split(':');
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const secs = seconds || '00';
    return `${String(hour12).padStart(2, '0')}:${minutes}:${secs} ${ampm}`;
  };

  const formatTime = (timestamp, timezone = "America/Chicago") => {
    const date = parseTimestamp(timestamp);
    
    // If showFullTimestamps is enabled, return full timestamp respecting time format
    if (showFullTimestamps) {
      const options = { timeZone: timezone };
      const dateInTimezone = new Date(date.toLocaleString("en-US", options));
      
      const year = dateInTimezone.getFullYear();
      const month = String(dateInTimezone.getMonth() + 1).padStart(2, '0');
      const day = String(dateInTimezone.getDate()).padStart(2, '0');
      
      if (timeFormat === "12h") {
        // 12-hour format with AM/PM
        const hours = dateInTimezone.getHours();
        const hour12 = hours % 12 || 12;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const minutes = String(dateInTimezone.getMinutes()).padStart(2, '0');
        const seconds = String(dateInTimezone.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${String(hour12).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`;
      } else {
        // 24-hour format
        const hours = String(dateInTimezone.getHours()).padStart(2, '0');
        const minutes = String(dateInTimezone.getMinutes()).padStart(2, '0');
        const seconds = String(dateInTimezone.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      }
    }
    
    // Original format logic
    const options = { timeZone: timezone };
    const now = new Date();
    const todayInTimezone = new Date(now.toLocaleString("en-US", options));
    const yesterdayInTimezone = new Date(todayInTimezone);
    yesterdayInTimezone.setDate(yesterdayInTimezone.getDate() - 1);

    const timeFormatOptions = { 
      timeZone: timezone,
      hour: "2-digit", 
      minute: "2-digit", 
      second: "2-digit",
      hour12: timeFormat === "12h"
    };
    
    const dateTimeFormatOptions = {
      timeZone: timezone,
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== todayInTimezone.getFullYear() ? "numeric" : undefined,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: timeFormat === "12h"
    };

    const dateInTimezone = new Date(date.toLocaleString("en-US", options));
    if (
      dateInTimezone.getDate() === todayInTimezone.getDate() &&
      dateInTimezone.getMonth() === todayInTimezone.getMonth() &&
      dateInTimezone.getFullYear() === todayInTimezone.getFullYear()
    ) {
      return date.toLocaleString("en-US", timeFormatOptions);
    } else if (
      dateInTimezone.getDate() === yesterdayInTimezone.getDate() &&
      dateInTimezone.getMonth() === yesterdayInTimezone.getMonth() &&
      dateInTimezone.getFullYear() === yesterdayInTimezone.getFullYear()
    ) {
      return `Yesterday ${date.toLocaleString("en-US", timeFormatOptions)}`;
    } else {
      return date.toLocaleString("en-US", dateTimeFormatOptions);
    }
  };

  const highlightText = (text, searchQuery) => {
    let characters = text.split('');
    let spans = Array(characters.length).fill(null);

    if (keywords?.length) {
      keywords.forEach(keyword => {
        const regex = new RegExp(keyword, 'gi');
        let match;
        while ((match = regex.exec(text)) !== null) {
          for (let i = match.index; i < match.index + match[0].length; i++) {
            if (!spans[i]) spans[i] = { text: characters[i], isKeyword: true };
          }
        }
      });
    }

    if (searchQuery) {
      const regex = new RegExp(searchQuery, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        for (let i = match.index; i < match.index + match[0].length; i++) {
          if (!spans[i]) spans[i] = { text: characters[i], isSearch: true };
          else spans[i] = { ...spans[i], isSearch: true };
        }
      }
    }

    let result = [];
    let currentSpan = null;

    for (let i = 0; i < characters.length; i++) {
      if (!spans[i]) {
        if (currentSpan) {
          result.push(currentSpan);
          currentSpan = null;
        }
        result.push(characters[i]);
        continue;
      }

      const { text, isKeyword, isSearch } = spans[i];
      const className = `${isKeyword ? 'underline font-bold' : ''} ${isSearch ? 'bg-yellow-200' : ''}`.trim();

      if (!currentSpan || currentSpan.props.className !== className) {
        if (currentSpan) result.push(currentSpan);
        currentSpan = <span key={`span-${i}`} className={className}>{text}</span>;
      } else {
        currentSpan = React.cloneElement(currentSpan, { children: currentSpan.props.children + text });
      }
    }

    if (currentSpan) result.push(currentSpan);
    return result;
  };

  const filteredMessages = messages.filter((message) => {
    const channelActive = activeChannels[message.channel];
    const matchesSearch = searchQuery
      ? message.message.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    const matchesKeywords =
      activeKeywords.size === 0 ||
      [...activeKeywords].some((keyword) =>
        message.message.toLowerCase().includes(keyword.toLowerCase())
      );
    return channelActive && matchesSearch && matchesKeywords;
  });

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
    setIsAtBottom(scrollHeight - scrollTop === clientHeight);
  };

  useEffect(() => {
    if (messages.length > previousMessages.length) setNewMessages(true);
  }, [messages, previousMessages]);

  useEffect(() => {
    setPreviousMessages(messages);
  }, [messages]);

  // Auto-scroll only on initial load or when user is at bottom
  useEffect(() => {
    if (isInitialLoad && messages.length > 0) {
      // On initial load, scroll to bottom
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
      setIsInitialLoad(false);
    } else if (isAtBottom && newMessages && messagesEndRef.current) {
      // Only auto-scroll if user is at bottom and new messages arrive
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      setNewMessages(false);
    }
  }, [messages, isAtBottom, newMessages, isInitialLoad]);

  const LOCAL_STORAGE_KEYS = {
    showTime: "showTime",
    showCar: "showCar",
    showChannel: "showChannel",
    showPerson: "showPerson",
    timeFilter: "timeFilter" 
  };

  const getInitialVisibilityState = (key, defaultValue) =>
    localStorage.getItem(key) === null
      ? defaultValue
      : JSON.parse(localStorage.getItem(key));

  // Visibility toggles for metadata in the inbox header row
  // Defaults: show timing and channel, hide tag and person
  const [showTime, setShowTime] = useState(() =>
    getInitialVisibilityState(LOCAL_STORAGE_KEYS.showTime, true)
  );
  const [showCar, setShowCar] = useState(() =>
    getInitialVisibilityState(LOCAL_STORAGE_KEYS.showCar, false)
  );
  const [showChannel, setShowChannel] = useState(() =>
    getInitialVisibilityState(LOCAL_STORAGE_KEYS.showChannel, true)
  );
  const [showPerson, setShowPerson] = useState(() =>
    getInitialVisibilityState(LOCAL_STORAGE_KEYS.showPerson, false)
  );

  useEffect(() => localStorage.setItem(LOCAL_STORAGE_KEYS.showTime, JSON.stringify(showTime)), [showTime]);
  useEffect(() => localStorage.setItem(LOCAL_STORAGE_KEYS.showCar, JSON.stringify(showCar)), [showCar]);
  useEffect(() => localStorage.setItem(LOCAL_STORAGE_KEYS.showChannel, JSON.stringify(showChannel)), [showChannel]);
  useEffect(() => localStorage.setItem(LOCAL_STORAGE_KEYS.showPerson, JSON.stringify(showPerson)), [showPerson]);
  
  useEffect(() => {
    if (!isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {/* Mobile Header */}
      {isMobile && (
        <div className={`sticky top-0 z-20 flex items-center justify-between p-2 border-b transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <button 
            onClick={toggleSidebar}
            className={`p-2 rounded-md transition-colors duration-200 ${
              isDarkMode 
                ? 'text-gray-400 hover:bg-gray-800' 
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className={`text-lg font-semibold ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>{branding.organizationName}</h1>
          <div className="w-8" /> {/* Spacer */}
        </div>
      )}

      {/* Top Bar - spans full width at the top */}
      {!isFullscreen && !isMobile && (
        <TopBar
          branding={branding}
          timezone={timezone}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          timeFilter={timeFilter}
          setTimeFilter={setTimeFilter}
          TIME_FILTERS={TIME_FILTERS}
          showTime={showTime}
          setShowTime={setShowTime}
          showCar={showCar}
          setShowCar={setShowCar}
          showChannel={showChannel}
          setShowChannel={setShowChannel}
          showPerson={showPerson}
          setShowPerson={setShowPerson}
          isMobile={isMobile}
          edgeServerEndpoint={edgeServerEndpoint}
          userRole={userRole}
          showFullTimestamps={showFullTimestamps}
          setShowFullTimestamps={setShowFullTimestamps}
          onFilterClick={() => {}}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          toggleTheme={toggleTheme}
          timeFormat={timeFormat}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onDocumentationClick={() => window.open('/user-guide', '_blank')}
          isAlertsVisible={isAlertsVisible}
          setIsAlertsVisible={setIsAlertsVisible}
          isSidebarVisible={isSidebarVisible}
          setIsSidebarVisible={setIsSidebarVisible}
          sidebarWidth={isSidebarVisible ? sidebarWidth : 250} // Use minimum width when hidden
          messagesWidth={messagesWidth}
          alertsWidth={isAlertsVisible ? alertsWidth : 200} // Use minimum width when hidden
          windowWidth={windowWidth}
        />
      )}

      {/* Quick Show Buttons - Left Edge */}
      {!isMobile && !isFullscreen && !isSidebarVisible && (
        <button
          onClick={toggleSidebarQuick}
          className={`fixed left-0 top-[calc(4rem+50%)] -translate-y-1/2 z-50 p-2 rounded-r-lg transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-r border-gray-700' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border-r border-gray-300'
          } shadow-lg hover:shadow-xl`}
          title="Show Channels"
        >
          <ChevronRightIcon size={20} />
        </button>
      )}
      
      {/* Quick Show Buttons - Right Edge */}
      {!isMobile && !isFullscreen && !isAlertsVisible && (
        <button
          onClick={toggleAlertsQuick}
          className={`fixed right-0 top-[calc(4rem+50%)] -translate-y-1/2 z-50 p-2 rounded-l-lg transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-l border-gray-700' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border-l border-gray-300'
          } shadow-lg hover:shadow-xl`}
          title="Show Insights"
        >
          <Lightbulb size={20} />
        </button>
      )}

      {/* Content Area - Sidebar, Messages, and Insights */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {!isMobile && !isFullscreen && (
          <ResizablePanel
            defaultWidth={288}
            minWidth={200}
            maxWidth={600}
            side="left"
            isVisible={isSidebarVisible}
            storageKey="sidebarWidth"
            isDarkMode={isDarkMode}
            onResize={setSidebarWidth}
            usePercentageConstraints={true}
          >
            <div
              className={`sidebar-container h-full transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
              }`}
            >
              <TeamsSidebar
                isDarkMode={isDarkMode}
                timezone={timezone}
                toggleTheme={toggleTheme}
                setIsDarkMode={setIsDarkMode}
                channels={channels}
                setChannels={setChannels}
                channelColors={channelColors}
                activeChannels={activeChannels}
                setActiveChannels={setActiveChannels}
                activeKeywords={activeKeywords}
                toggleKeyword={toggleKeyword}
                isVolumeOn={isVolumeOn}
                setIsVolumeOn={setIsVolumeOn}
                keywordCounts={keywordCounts}
                channelMessageCounts={channelMessageCounts}
                API_BASE_URL={API_BASE_URL}
                isMobile={isMobile}
                closeSidebar={toggleSidebarQuick}
                onDocumentationClick={() => window.open('/user-guide', '_blank')}
                branding={branding}
              />
            </div>
          </ResizablePanel>
        )}
        
        {/* Mobile Sidebar */}
        {isMobile && (
          <div
            className={`sidebar-container transition-transform duration-300 ease-in-out
              fixed top-0 left-0 h-full w-72 z-50 transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
              } 
              ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
          >
            <TeamsSidebar
              isDarkMode={isDarkMode}
              timezone={timezone}
              toggleTheme={toggleTheme}
              setIsDarkMode={setIsDarkMode}
              channels={channels}
              setChannels={setChannels}
              channelColors={channelColors}
              activeChannels={activeChannels}
              setActiveChannels={setActiveChannels}
              activeKeywords={activeKeywords}
              toggleKeyword={toggleKeyword}
              isVolumeOn={isVolumeOn}
              setIsVolumeOn={setIsVolumeOn}
              keywordCounts={keywordCounts}
              channelMessageCounts={channelMessageCounts}
              API_BASE_URL={API_BASE_URL}
              isMobile={isMobile}
              closeSidebar={() => setIsSidebarOpen(false)}
              onDocumentationClick={() => window.open('/user-guide', '_blank')}
              branding={branding}
            />
          </div>
        )}

        {/* Messages and Alerts Container */}
        <div className="flex-1 flex flex-row overflow-hidden">
          {/* Messages - Center */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Notification Banner */}
            {!isMobile && <NotificationBanner edgeServerEndpoint={edgeServerEndpoint} isDarkMode={isDarkMode} />}

            {/* Filter Indicator Banner */}
            {!isMobile && (startDate || endDate || (timeFilter && timeFilter !== TIME_FILTERS.ALL)) && (
              <div className={`sticky top-[73px] z-10 ${isDarkMode ? 'bg-yellow-900/20 border-yellow-700/30' : 'bg-yellow-50 border-yellow-200'} border-b px-4 py-2 flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    Showing filtered results
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                    {startDate && endDate 
                      ? `${startDate}${startTime ? ' ' + formatTimeDisplay(startTime) : ''} to ${endDate}${endTime ? ' ' + formatTimeDisplay(endTime) : ''}`
                      : timeFilter && timeFilter !== TIME_FILTERS.ALL
                      ? timeFilter
                      : ''
                    }
                  </span>
                </div>
                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setStartTime('');
                    setEndTime('');
                    setTimeFilter(TIME_FILTERS.ALL);
                  }}
                  className={`text-sm font-medium underline ${isDarkMode ? 'text-yellow-300 hover:text-yellow-200' : 'text-yellow-700 hover:text-yellow-800'}`}
                >
                  Clear Filter
                </button>
              </div>
            )}

            {/* Messages Content */}
            <div className={`flex-1 flex flex-col overflow-hidden border-l border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <FullscreenMessages
              setMessages={setMessages}
              edgeServerEndpoint={edgeServerEndpoint}
              isDarkMode={isDarkMode}
              messages={isMobile ? mobileMessages : desktopMessages}
              totalMessages={totalFilteredMessages}
              channels={channels}
              showTime={showTime}
              showCar={showCar}
              showChannel={showChannel}
              showPerson={showPerson}
              formatTime={formatTime}
              timezone={timezone}
              timeFormat={timeFormat}
              activeAudioUrl={activeAudioUrl}
              setActiveAudioUrl={setActiveAudioUrl}
              highlightText={highlightText}
              searchQuery={searchQuery}
              handlePlayAudio={handlePlayAudio}
              AudioIcon={AudioIcon}
              isFullscreen={isFullscreen}
              onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
              isMobile={isMobile}
              reverseSort={reverseSort}
              currentPage={currentPage}
              // Only enable infinite scroll in continuous mode
              onLoadMore={inboxViewMode === 'continuous' ? loadMoreMessages : null}
              hasMoreMessages={inboxViewMode === 'continuous' ? hasMoreMessages : false}
              isLoadingMore={inboxViewMode === 'continuous' ? isLoadingMore : false}
              inboxViewMode={inboxViewMode}
            />

            {/* Footer pagination when inbox is in pagination mode */}
            {inboxViewMode === 'pagination' && (
              <FooterPagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                getFilteredMessages={isMobile ? getFilteredMessagesForMobile : getFilteredMessages}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                getTotalPages={getTotalPages}
                recordsPerPage={recordsPerPage}
                setRecordsPerPage={setRecordsPerPage}
                isMobile={isMobile}
                edgeServerEndpoint={edgeServerEndpoint}
                reverseSort={reverseSort}
              />
            )}
            </div>
          </div>

          {/* Alerts Panel - Right */}
          {!isFullscreen && (
            <>
              {isMobile ? (
                // Mobile: Alerts as overlay
                isAlertsVisible && (
                  <>
                    <div
                      className="fixed inset-0 bg-black/50 z-50"
                      onClick={() => setIsAlertsVisible(false)}
                    />
                    <div className="fixed right-0 top-0 h-full w-72 z-50">
                      <AlertsPanel
                        isDarkMode={isDarkMode}
                        edgeServerEndpoint={edgeServerEndpoint}
                        isMobile={isMobile}
                        isVisible={isAlertsVisible}
                        onClose={() => setIsAlertsVisible(false)}
                        formatTime={(timestamp) => formatTime(timestamp, timezone)}
                        timezone={timezone}
                        timeFormat={timeFormat}
                      />
                    </div>
                  </>
                )
              ) : (
                // Desktop: Alerts as resizable side panel
                <ResizablePanel
                  defaultWidth={288}
                  minWidth={200}
                  maxWidth={600}
                  side="right"
                  isVisible={isAlertsVisible}
                  storageKey="alertsWidth"
                  isDarkMode={isDarkMode}
                  onResize={setAlertsWidth}
                  usePercentageConstraints={true}
                >
                    <AlertsPanel
                      isDarkMode={isDarkMode}
                      edgeServerEndpoint={edgeServerEndpoint}
                      isMobile={isMobile}
                      isVisible={isAlertsVisible}
                      onClose={toggleAlertsQuick}
                      formatTime={(timestamp) => formatTime(timestamp, timezone)}
                      timezone={timezone}
                      timeFormat={timeFormat}
                    />
                </ResizablePanel>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      {/* MFA Reminder Modal */}
      <MFAReminderModal
        isOpen={showMfaReminder}
        onClose={() => {
          setShowMfaReminder(false);
          sessionStorage.setItem('mfa_reminder_dismissed', 'true');
        }}
        onSetup={() => {
          setShowMfaReminder(false);
          sessionStorage.removeItem('mfa_reminder_dismissed');
        }}
        edgeServerEndpoint={edgeServerEndpoint}
        isDarkMode={isDarkMode}
        user={authUser}
      />

    </div>
  );
};

export default LiveCommunications;